/**
 * Drag-to-dismiss for a bottom sheet (BON-534).
 *
 * Bound to the HANDLE AND HEADER ONLY, never to the scrolling list. That is the
 * whole trick: a sheet that listens for drags over its content has to guess,
 * on every gesture, whether the user meant to scroll the list or move the
 * sheet, and it guesses wrong often enough to feel broken. Restricting the
 * gesture to the strip that is not scrollable removes the ambiguity entirely.
 *
 * Downward only, and only past a threshold — a short tug springs back, so a
 * mis-grab costs nothing. The sheet stays dismissible by the close button, the
 * scrim and Escape regardless, so nothing depends on the gesture landing.
 */
import { useCallback, useRef, useState } from 'react';

/** How far down the sheet must travel before letting go dismisses it. */
const DISMISS_AFTER_PX = 90;

export interface SheetDrag {
  /** Live offset in px; 0 when at rest. Apply as `translateY`. */
  offset: number;
  /** True while a finger is down, so the caller can drop its transition. */
  dragging: boolean;
  /** Spread onto the handle/header strip. */
  handleProps: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
    style: React.CSSProperties;
  };
}

export function useSheetDrag(onDismiss: () => void): SheetDrag {
  const startY = useRef<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    startY.current = e.clientY;
    setDragging(true);
    // Keep receiving moves even if the finger leaves the strip.
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (startY.current === null) return;
    // Downward only: dragging up would lift the sheet off the bottom edge.
    setOffset(Math.max(0, e.clientY - startY.current));
  }, []);

  const end = useCallback(
    (e: React.PointerEvent) => {
      if (startY.current === null) return;
      const travelled = Math.max(0, e.clientY - startY.current);
      startY.current = null;
      setDragging(false);
      setOffset(0);
      if (travelled >= DISMISS_AFTER_PX) onDismiss();
    },
    [onDismiss],
  );

  return {
    offset,
    dragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerCancel: end,
      // Claim vertical gestures so the browser does not scroll the page under
      // the sheet while the strip is being dragged.
      style: { touchAction: 'none' },
    },
  };
}
